import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart3, Clock, Check, Trash2 } from "lucide-react";

const timeLeft = (expiresAt) => {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "Termine";
  const hours = Math.floor(diff / 3600000);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}j restant${days > 1 ? "s" : ""}`;
  }
  if (hours > 0) return `${hours}h restante${hours > 1 ? "s" : ""}`;
  const mins = Math.floor(diff / 60000);
  return `${mins}min`;
};

export const PollCard = ({ poll, onVote, onDelete }) => {
  const { user } = useAuth();
  const [voting, setVoting] = useState(false);
  const [localPoll, setLocalPoll] = useState(poll);

  const hasVoted = !!localPoll.user_vote;
  const isExpired = localPoll.is_expired || new Date(localPoll.expires_at) < new Date();
  const showResults = hasVoted || isExpired;
  const isCreator = user?.id === localPoll.creator_id;
  const total = localPoll.total_votes || 0;

  const handleVote = async (optionId) => {
    if (!onVote || voting || isExpired) return;
    setVoting(true);
    try {
      const updated = await onVote(localPoll.id, optionId);
      if (updated) setLocalPoll(updated);
    } catch (err) {
      console.error(err);
    }
    setVoting(false);
  };

  const handleDelete = () => {
    if (onDelete && window.confirm("Supprimer ce sondage ?")) {
      onDelete(localPoll.id);
    }
  };

  return (
    <div
      className="bg-card border border-border rounded-3xl p-5 hover:border-[#FF6B35]/30 transition-colors duration-300 shadow-sm"
      data-testid={`poll-card-${localPoll.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7000FF] to-[#FF8FA3] flex items-center justify-center shrink-0">
          {localPoll.creator_avatar ? (
            <img src={localPoll.creator_avatar} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="text-white font-heading font-bold text-sm">
              {(localPoll.creator_pseudo || "?")[0].toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-heading font-semibold text-sm text-card-foreground truncate">
              {localPoll.artist_name || localPoll.creator_pseudo}
            </p>
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#7000FF]/10 text-[#7000FF] border border-[#7000FF]/20">
              <BarChart3 className="w-3 h-3" />
              Sondage
            </span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeLeft(localPoll.expires_at)}
            </span>
            <span>{total} vote{total !== 1 ? "s" : ""}</span>
          </div>
        </div>
        {isCreator && onDelete && (
          <button
            onClick={handleDelete}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-50 transition-colors"
            data-testid={`poll-delete-${localPoll.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Question */}
      <h3 className="font-heading font-bold text-base text-card-foreground mb-3">
        {localPoll.question}
      </h3>

      {/* Options */}
      <div className="space-y-2">
        {localPoll.options.map((opt) => {
          const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
          const isSelected = localPoll.user_vote === opt.id;
          const isWinner = showResults && total > 0 &&
            opt.votes === Math.max(...localPoll.options.map(o => o.votes));

          return (
            <button
              key={opt.id}
              onClick={() => !showResults && handleVote(opt.id)}
              disabled={voting || isExpired || hasVoted}
              className={`relative w-full text-left rounded-xl px-4 py-3 transition-all duration-300 overflow-hidden ${
                showResults
                  ? "cursor-default"
                  : "cursor-pointer hover:border-[#FF6B35]/40 active:scale-[0.99]"
              } ${
                isSelected
                  ? "border-2 border-[#FF6B35] bg-[#FF6B35]/5"
                  : "border border-border bg-foreground/50"
              }`}
              data-testid={`poll-option-${opt.id}`}
            >
              {/* Progress bar */}
              {showResults && (
                <div
                  className={`absolute inset-y-0 left-0 transition-all duration-700 ease-out rounded-xl ${
                    isWinner ? "bg-[#FF6B35]/15" : "bg-border/40"
                  }`}
                  style={{ width: `${pct}%` }}
                />
              )}

              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {isSelected && (
                    <Check className="w-4 h-4 text-[#FF6B35] shrink-0" />
                  )}
                  <span className={`text-sm truncate ${
                    isSelected ? "font-semibold text-[#FF6B35]" : "text-card-foreground/80"
                  } ${isWinner && showResults ? "font-semibold" : ""}`}>
                    {opt.text}
                  </span>
                </div>
                {showResults && (
                  <span className={`text-xs font-mono shrink-0 ml-2 ${
                    isWinner ? "font-bold text-[#FF6B35]" : "text-muted-foreground"
                  }`}>
                    {pct}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      {hasVoted && !isExpired && (
        <p className="text-[10px] text-muted-foreground mt-3 text-center">
          +3 credits pour ton vote !
        </p>
      )}
    </div>
  );
};
